import React, { useState, useEffect, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/contexts/RoleContext";
import { MainLayout } from "@/layouts/MainLayout";
import { DataTable, FilterPanel, Toast, CallAgentButton } from "@/components/common";
import type { Column, FilterConfig } from "@/components/common";
import { loansApi, entityRoleMappingApi, subAgentsApi, clickToCallApi } from "@/api";
import type { Loan } from "@/types/loans.types";
import type { AgentEntity } from "@/types/entityRoleMapping.types";
import { exportToExcel, type ExportColumn } from "@/utils/exportToExcel";
import { fetchAllForExport, EXPORT_PAGE_SIZE } from "@/utils/fetchAllForExport";
import { ExcelIcon } from "@/components/icons";
import styles from "./Loans.module.css";

const SUB_STATUS_OPTIONS = [
  { label: "FRESH_LOAN", value: "FRESH_LOAN" },
  { label: "LOAN_DETAILS_SUBMITTED", value: "LOAN_DETAILS_SUBMITTED" },
  { label: "UNDER_REVIEW", value: "UNDER_REVIEW" },
  { label: "KYC_SUCCESS", value: "KYC_SUCCESS" },
  { label: "KYC_REJECTED", value: "KYC_REJECTED" },
  { label: "BANK_ADDED", value: "BANK_ADDED" },
  { label: "SIGN_AGREEMENT", value: "SIGN_AGREEMENT" },
  { label: "DISBURSED", value: "DISBURSED" },
  { label: "LOAN_REJECTED", value: "LOAN_REJECTED" },
  { label: "CANCELLED", value: "CANCELLED" },
  { label: "SKIP", value: "SKIP" },
];

const LOAN_STATUS_OPTIONS = [
  { label: "Pending", value: "Pending" },
  { label: "Approved", value: "Approved" },
  { label: "Active", value: "Active" },
  { label: "Closed", value: "Closed" },
  { label: "Rejected", value: "Rejected" },
  { label: "Esign Completed", value: "Esign Completed" },
];

const LENDER_OPTIONS = [{ label: "SPCBL", value: "SPCBL" }];

const SEARCH_TYPE_OPTIONS = [
  { label: "Loan ID", value: "loan_id" },
  { label: "Mobile Number", value: "mob_num" },
  { label: "User ID", value: "user_id" },
  { label: "Name", value: "name" },
];

/** Add one day to a date string (YYYY-MM-DD) for inclusive "to" date filtering */
function addOneDay(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  date.setDate(date.getDate() + 1);
  return date.toISOString().split("T")[0];
}

export const Loans: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { selectedRole, loading: roleLoading } = useRole();

  const agentIdOverride = searchParams.get("agentId") || null;
  const agentName = searchParams.get("agentName") || null;
  const [loans, setLoans] = useState<Loan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [hasMore, setHasMore] = useState<boolean | undefined>(undefined);

  const [exportLimit, setExportLimit] = useState<number>(10);
  const [exporting, setExporting] = useState(false);

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [filters, setFilters] = useState<Record<string, unknown>>(() => {
    const sub_status = searchParams.getAll("sub_status");
    const loan_status = searchParams.getAll("loan_status");
    const date_range = searchParams.getAll("date_range");
    return {
      sub_status: sub_status.length > 0 ? sub_status : [],
      loan_status: loan_status.length > 0 ? loan_status : [],
      lender_id: searchParams.get("lender_id") || null,
      filter_agent_id: searchParams.get("filter_agent_id") || null,
      search_text: searchParams.get("search_text") || "",
      search_type: searchParams.get("search_type") || "all",
      date_range_field: searchParams.get("date_range_field") || null,
      date_range: date_range.length === 2 ? date_range : null,
      assigned_to_agent: searchParams.get("assigned_to_agent") || null,
    };
  });

  const [searchTextInput, setSearchTextInput] = useState(
    () => searchParams.get("search_text") || ""
  );

  const [availableAgents, setAvailableAgents] = useState<
    Array<{ role_id: string; agent_name: string; agent_id: string }>
  >([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [subAgentOptions, setSubAgentOptions] = useState<
    Array<{ label: string; value: string }>
  >([]);

  const [callToast, setCallToast] = useState({ show: false, message: "" });
  const [callingRowId, setCallingRowId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/agent-login");
    }
  }, [isAuthenticated, navigate]);

  const handleCallCustomer = useCallback(async (row: Loan, callType: "ext" | "phone") => {
    // Always use the logged-in agent's ID for click-to-call,
    // not the sub-agent override, since it's the logged-in agent's phone that should ring
    const agentId =
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId");

    if (!agentId) {
      setCallToast({ show: true, message: "Agent ID not found" });
      return;
    }

    if (!row.mob_num) {
      setCallToast({ show: true, message: "Customer phone number not available" });
      return;
    }

    setCallingRowId(row.loan_id);
    try {
      const response = await clickToCallApi.clickToCall({
        agent_id: agentId,
        customer_number: "91" + String(row.mob_num),
        cp_team: "sales_team",
        call_type: callType,
      });
      setCallToast({ show: true, message: response.message || "Call initiated successfully" });
    } catch {
      setCallToast({ show: true, message: "Failed to initiate call" });
    } finally {
      setCallingRowId(null);
    }
  }, []);

  useEffect(() => {
    const fetchSubAgents = async () => {
      const agentId =
        agentIdOverride ||
        localStorage.getItem("agent_id") ||
        localStorage.getItem("agentId");
      if (!agentId) return;

      try {
        const response = await subAgentsApi.getSubAgents({ agent_id: agentId });
        if (response.response && response.response.length > 0) {
          const flattenAgents = (
            agents: typeof response.response,
          ): Array<{ label: string; value: string }> => {
            const result: Array<{ label: string; value: string }> = [];
            agents.forEach((agent) => {
              const name = [agent.fname, agent.mname, agent.lname]
                .filter(Boolean)
                .join(" ");
              result.push({
                label: name || agent.agent_id,
                value: agent.agent_id,
              });
              if (agent.children && agent.children.length > 0) {
                result.push(...flattenAgents(agent.children));
              }
            });
            return result;
          };
          setSubAgentOptions(flattenAgents(response.response));
        }
      } catch (err) {
        console.error("Failed to fetch sub-agents:", err);
      }
    };

    if (isAuthenticated) {
      fetchSubAgents();
    }
  }, [isAuthenticated, agentIdOverride]);

  useEffect(() => {
    const fetchAvailableAgents = async () => {
      if (!selectedRole || selectedRole.role_type !== "DOCUMENT_VERIFIER") {
        setAvailableAgents([]);
        return;
      }

      const agentId =
        agentIdOverride ||
        localStorage.getItem("agent_id") ||
        localStorage.getItem("agentId");
      if (!agentId) return;

      setLoadingAgents(true);
      try {
        const response = await entityRoleMappingApi.getAgents({
          agent_id: agentId,
          role_type: "DOCUMENT_VERIFIER",
        });

        if (response.status === "Success" && response.response) {
          const flattenAgents = (
            nodes: typeof response.response.tree,
          ): Array<{
            role_id: string;
            agent_name: string;
            agent_id: string;
          }> => {
            const agents: Array<{
              role_id: string;
              agent_name: string;
              agent_id: string;
            }> = [];
            nodes.forEach((node) => {
              agents.push({
                role_id: node.role_id,
                agent_name: node.agent_name,
                agent_id: node.agent_id,
              });
              if (node.children && node.children.length > 0) {
                agents.push(...flattenAgents(node.children));
              }
            });
            return agents;
          };

          const agents = flattenAgents(response.response.tree);
          setAvailableAgents(agents);
        }
      } catch (err) {
        console.error("Failed to fetch available agents:", err);
      } finally {
        setLoadingAgents(false);
      }
    };

    fetchAvailableAgents();
  }, [selectedRole, agentIdOverride]);

  // Helper to remove undefined keys from object
  const cleanFilters = (
    obj: Record<string, unknown>,
  ): Record<string, unknown> => {
    return Object.entries(obj).reduce(
      (acc, [key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, unknown>,
    );
  };

  const fetchLoans = useCallback(async () => {
    const agentId =
      agentIdOverride ||
      localStorage.getItem("agent_id") ||
      localStorage.getItem("agentId");

    if (!agentId) {
      setError("Agent ID not found");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const dateRangeField = (filters.date_range_field as string) || null;
      const dateRange: [string | null, string | null] =
        Array.isArray(filters.date_range) && filters.date_range.length === 2
          ? [
              filters.date_range[0] as string | null,
              filters.date_range[1] as string | null,
            ]
          : [null, null];

      const createdAtRange: [string | null, string | null] =
        dateRangeField === "created_at" ? dateRange : [null, null];
      const updatedAtRange: [string | null, string | null] =
        dateRangeField === "updated_at" ? dateRange : [null, null];
      const disbursedDateRange: [string | null, string | null] =
        dateRangeField === "disbursed_date"
          ? [dateRange[0], addOneDay(dateRange[1])]
          : [null, null];

      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string;
      const isAllSearch =
        (!searchType || searchType === "all") && searchText.length > 0;

      const rawCommonFilters = {
        sub_status:
          Array.isArray(filters.sub_status) && filters.sub_status.length > 0
            ? (filters.sub_status as string[])
            : undefined,
        loan_status:
          Array.isArray(filters.loan_status) && filters.loan_status.length > 0
            ? (filters.loan_status as string[])
            : undefined,
        lender_id: filters.lender_id
          ? (filters.lender_id as string)
          : undefined,
      };

      const commonFilters = cleanFilters(rawCommonFilters);

      if (selectedRole) {
        const sortByValue: { [key: string]: 1 | -1 } | undefined = sortBy
          ? { [`erm.${sortBy}`]: sortOrder === "asc" ? 1 : -1 }
          : undefined;
        const effectiveRoleId = filters.assigned_to_agent
          ? (filters.assigned_to_agent as string)
          : selectedRole.role_id;

        const baseEntityRequest = {
          role_id: effectiveRoleId,
          entity_type: "loan_id",
          role_type: selectedRole.role_type,
          page,
          page_size: pageSize,
          sort_by: sortByValue,
          ranges: {
            created_at: createdAtRange,
            updated_at: updatedAtRange,
            disbursed_date: disbursedDateRange,
          },
        };

        if (isAllSearch) {
          const searchTargets = ["loan_id", "mob_num", "user_id", "name"];
          const requests = searchTargets.map((type) => {
            const specificFilters = { ...commonFilters };
            let specificText = undefined;

            if (type === "name") {
              specificText = searchText;
            } else {
              (specificFilters as any)[type] = searchText;
            }

            return entityRoleMappingApi.getAgentEntities({
              ...baseEntityRequest,
              filters: specificFilters,
              text: specificText,
            });
          });

          const responses = await Promise.all(requests);
          let mergedLoans: Loan[] = [];
          const seenLoanIds = new Set<string>();
          let anyHasMore = false;
          let sawHasMoreField = false;

          responses.forEach((res) => {
            if (res.status === "Success" && res.response) {
              const loans = res.response.response.map((entity: AgentEntity) =>
                convertEntityToLoan(entity),
              );
              loans.forEach((loan) => {
                if (!seenLoanIds.has(loan.loan_id)) {
                  seenLoanIds.add(loan.loan_id);
                  mergedLoans.push(loan);
                }
              });
              if (res.response.pagination?.has_more !== undefined) {
                sawHasMoreField = true;
                if (res.response.pagination.has_more) anyHasMore = true;
              }
            }
          });

          setLoans(mergedLoans);
          setHasMore(sawHasMoreField ? anyHasMore : undefined);
        } else {
          const entityFilters = cleanFilters({
            ...commonFilters,
            mob_num:
              searchType === "mob_num" && searchText ? searchText : undefined,
            user_id:
              searchType === "user_id" && searchText ? searchText : undefined,
            loan_id:
              searchType === "loan_id" && searchText ? searchText : undefined,
          });

          const entityRequest = {
            ...baseEntityRequest,
            filters: entityFilters,
            text:
              (!searchType || searchType === "name") && searchText
                ? searchText
                : undefined,
          };

          const entityResponse =
            await entityRoleMappingApi.getAgentEntities(entityRequest);

          if (entityResponse.status === "Success" && entityResponse.response) {
            const convertedLoans = entityResponse.response.response.map(
              (entity: AgentEntity) => convertEntityToLoan(entity),
            );
            setLoans(convertedLoans);
            setHasMore(entityResponse.response.pagination?.has_more);
          } else {
            setError(entityResponse.message || "Failed to fetch loans");
          }
        }
      } else {
        const sortByValue: { [key: string]: 1 | -1 } = sortBy
          ? { [sortBy]: sortOrder === "asc" ? 1 : -1 }
          : { created_at: -1 };
        const effectiveAgentId = (filters.filter_agent_id as string) || agentId;

        const baseRequest = {
          query_type: "new_get_agent_loans" as const,
          agent_id: effectiveAgentId,
          page,
          page_size: pageSize,
          ranges: {
            created_at: createdAtRange,
            updated_at: updatedAtRange,
            disbursed_date: disbursedDateRange,
          },
          sort_by: sortByValue,
        };

        if (isAllSearch) {
          const searchTargets = ["loan_id", "mob_num", "user_id", "name"];
          const requests = searchTargets.map((type) => {
            const specificFilters = { ...commonFilters };
            let specificText = undefined;

            if (type === "name") {
              specificText = searchText;
            } else if (type === "user_id") {
              (specificFilters as any)["l.user_id"] = searchText;
            } else {
              (specificFilters as any)[type] = searchText;
            }

            return loansApi.getLoanDetails({
              ...baseRequest,
              filters: specificFilters,
              text: specificText,
            });
          });

          const responses = await Promise.all(requests);
          let mergedLoans: Loan[] = [];
          const seenLoanIds = new Set<string>();
          let anyHasMore = false;
          let sawHasMoreField = false;

          responses.forEach((res) => {
            if (res.response) {
              res.response.forEach((loan) => {
                if (!seenLoanIds.has(loan.loan_id)) {
                  seenLoanIds.add(loan.loan_id);
                  mergedLoans.push(loan);
                }
              });
              if (res.pagination_metadata?.has_more !== undefined) {
                sawHasMoreField = true;
                if (res.pagination_metadata.has_more) anyHasMore = true;
              }
            }
          });
          setLoans(mergedLoans);
          setHasMore(sawHasMoreField ? anyHasMore : undefined);
        } else {
          const requestFilters = cleanFilters({
            ...commonFilters,
            loan_id:
              searchType === "loan_id" && searchText ? searchText : undefined,
            mob_num:
              searchType === "mob_num" && searchText ? searchText : undefined,
            "l.user_id":
              searchType === "user_id" && searchText ? searchText : undefined,
          });

          const request = {
            ...baseRequest,
            filters: requestFilters,
            text:
              (!searchType || searchType === "name") && searchText
                ? searchText
                : undefined,
          };

          const response = await loansApi.getLoanDetails(request);
          if (response.response) {
            setLoans(response.response);
            setHasMore(response.pagination_metadata?.has_more);
          } else {
            setError(response.message || "Failed to fetch loans");
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch loans");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    sortBy,
    sortOrder,
    filters,
    selectedRole,
    agentIdOverride,
  ]);

  useEffect(() => {
    if (isAuthenticated && !roleLoading) {
      fetchLoans();
    }
  }, [isAuthenticated, roleLoading, fetchLoans]);

  const convertEntityToLoan = (entity: AgentEntity): Loan =>
    ({
      loan_id: entity.loan_id,
      user_id: entity.user_id,
      agent_id: entity.assigned_agent_id || "",
      fname: entity.fname,
      mname: entity.mname,
      lname: entity.lname,
      mob_num: entity.user_mob_num,
      email: entity.user_email,
      loan_created_at: entity.loan_created_at,
      loan_updated_at: entity.loan_updated_at,
      sub_status: entity.sub_status,
      requested_amt: entity.requested_amt,
      lender_approved_amt: entity.lender_approved_amt,
      loan_tenure: entity.loan_tenure,
      payment_frequency: entity.payment_frequency,
      agent_name: entity.agent_name,
      parent_agent_name: entity.parent_agent_name,
      parent_agent_mob_num: entity.parent_agent_mob_num,
      assignment_date: entity.assignment_date,
      assigned_by: entity.assigned_by,
      assigned_agent_id: entity.assigned_agent_id,
      assigned_to_agent: entity.assigned_to_agent,
      ...(entity as unknown as Record<string, unknown>),
    }) as Loan;

  const handleSort = (key: string, order: "asc" | "desc") => {
    setSortBy(key);
    setSortOrder(order);
    setPage(1);
  };

  const handleFilterChange = (key: string, value: unknown) => {
    if (key === "search_text") {
      const text = typeof value === "string" ? value : "";
      setSearchTextInput(text);
      // When search text is cleared, also clear it from filters and URL params
      // so that reloading the page doesn't restore stale search results
      if (!text) {
        setFilters((prev) => ({ ...prev, search_text: "" }));
        setSearchParams((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("search_text");
          return newParams;
        });
      }
      return;
    }
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setPage(1);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (Array.isArray(value)) {
        newParams.delete(key);
        value.forEach((v) => newParams.append(key, String(v)));
      } else if (value !== null && value !== undefined && value !== "") {
        newParams.set(key, String(value));
      } else {
        newParams.delete(key);
      }
      return newParams;
    });
  };

  const handleSearchSubmit = (_key: string, value: unknown) => {
    const text = typeof value === "string" ? value : "";
    setFilters((prev) => ({
      ...prev,
      search_text: text,
    }));
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (text) {
        newParams.set("search_text", text);
      } else {
        newParams.delete("search_text");
      }
      return newParams;
    });
    setPage(1);
  };

  const handleResetFilters = () => {
    setSearchTextInput("");
    setFilters({
      sub_status: [],
      loan_status: [],
      lender_id: null,
      filter_agent_id: null,
      search_text: "",
      search_type: "all",
      date_range_field: null,
      date_range: null,
      assigned_to_agent: null,
    });
    setPage(1);
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      [
        "sub_status",
        "loan_status",
        "lender_id",
        "filter_agent_id",
        "search_text",
        "search_type",
        "date_range_field",
        "date_range",
        "assigned_to_agent",
      ].forEach((k) => newParams.delete(k));
      return newParams;
    });
  };

  const [changingAssignment, setChangingAssignment] = useState<string | null>(
    null,
  );
  const [newAssignedAgent, setNewAssignedAgent] = useState<string>("");

  const handleChangeAssignment = async (
    loanId: string,
    currentAssignedRoleId: string | undefined,
  ) => {
    if (!selectedRole || !newAssignedAgent) return;

    setChangingAssignment(loanId);
    try {
      const response = await entityRoleMappingApi.changeEntityMapping({
        entity_id: loanId,
        entity_type: "loan_id",
        role_type: selectedRole.role_type,
        assigned_by: selectedRole.role_id,
        assigned_to: newAssignedAgent,
      });

      if (response.status === "Success") {
        await fetchLoans();
        setNewAssignedAgent("");
        setChangingAssignment(null);
      } else {
        setError(response.message || "Failed to change assignment");
        setChangingAssignment(null);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to change assignment",
      );
      setChangingAssignment(null);
    }
  };

  const handleExportToExcel = async () => {
    const currentTableId = selectedRole
      ? `loans_${selectedRole.role_type}`
      : "loans";
    const savedConfigStr = localStorage.getItem(
      `table_config_${currentTableId}`,
    );
    let targetColumns = allColumns;

    if (savedConfigStr) {
      try {
        const config = JSON.parse(savedConfigStr);
        if (config.visibleColumns && Array.isArray(config.visibleColumns)) {
          const visibleSet = new Set(config.visibleColumns);
          targetColumns = allColumns.filter((col) => visibleSet.has(col.key));

          if (config.columnOrder && Array.isArray(config.columnOrder)) {
            targetColumns.sort((a, b) => {
              const indexA = config.columnOrder.indexOf(a.key);
              const indexB = config.columnOrder.indexOf(b.key);
              if (indexA === -1) return 1;
              if (indexB === -1) return -1;
              return indexA - indexB;
            });
          }
        }
      } catch (e) {
        console.error(
          "Error parsing saved config for export, using default.",
          e,
        );
        targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
      }
    } else {
      targetColumns = allColumns.filter((c) => c.defaultVisible !== false);
    }

    setExporting(true);
    setError("");

    try {
      const agentId =
        agentIdOverride ||
        localStorage.getItem("agent_id") ||
        localStorage.getItem("agentId");

      if (!agentId) throw new Error("Agent ID not found");

      let dataToExport: Loan[] = [];

      const dateRangeField = (filters.date_range_field as string) || null;
      const dateRange: [string | null, string | null] =
        Array.isArray(filters.date_range) && filters.date_range.length === 2
          ? [
              filters.date_range[0] as string | null,
              filters.date_range[1] as string | null,
            ]
          : [null, null];

      const createdAtRange: [string | null, string | null] =
        dateRangeField === "created_at" ? dateRange : [null, null];
      const updatedAtRange: [string | null, string | null] =
        dateRangeField === "updated_at" ? dateRange : [null, null];
      const disbursedDateRange: [string | null, string | null] =
        dateRangeField === "disbursed_date"
          ? [dateRange[0], addOneDay(dateRange[1])]
          : [null, null];

      const searchText = (filters.search_text as string) || "";
      const searchType = filters.search_type as string;
      const isAllSearch =
        (!searchType || searchType === "all") && searchText.length > 0;

      const rawCommonFilters = {
        sub_status:
          Array.isArray(filters.sub_status) && filters.sub_status.length > 0
            ? (filters.sub_status as string[])
            : undefined,
        loan_status:
          Array.isArray(filters.loan_status) && filters.loan_status.length > 0
            ? (filters.loan_status as string[])
            : undefined,
        lender_id: filters.lender_id
          ? (filters.lender_id as string)
          : undefined,
      };
      const commonFilters = cleanFilters(rawCommonFilters);

      if (selectedRole) {
        const sortByValue: { [key: string]: 1 | -1 } | undefined = sortBy
          ? { [`erm.${sortBy}`]: sortOrder === "asc" ? 1 : -1 }
          : undefined;
        const effectiveRoleId = filters.assigned_to_agent
          ? (filters.assigned_to_agent as string)
          : selectedRole.role_id;

        const baseEntityRequest = {
          role_id: effectiveRoleId,
          entity_type: "loan_id",
          role_type: selectedRole.role_type,
          sort_by: sortByValue,
          ranges: { created_at: createdAtRange, updated_at: updatedAtRange, disbursed_date: disbursedDateRange },
        };

        if (isAllSearch) {
          const searchTargets = ["loan_id", "mob_num", "user_id", "name"];
          const seenLoanIds = new Set<string>();
          for (const type of searchTargets) {
            const specificFilters = { ...commonFilters };
            let specificText: string | undefined = undefined;
            if (type === "name") specificText = searchText;
            else (specificFilters as any)[type] = searchText;

            const entities = await fetchAllForExport<AgentEntity>(
              async (page, pageSize) => {
                const res = await entityRoleMappingApi.getAgentEntities({
                  ...baseEntityRequest,
                  page,
                  page_size: pageSize,
                  filters: specificFilters,
                  text: specificText,
                });
                if (res.status !== "Success" || !res.response) return { rows: [] };
                return {
                  rows: res.response.response,
                  hasMore: res.response.pagination?.has_more,
                };
              },
              exportLimit,
              EXPORT_PAGE_SIZE,
            );

            entities.forEach((entity) => {
              if (!seenLoanIds.has(entity.loan_id)) {
                seenLoanIds.add(entity.loan_id);
                dataToExport.push(convertEntityToLoan(entity));
              }
            });
          }
        } else {
          const entityFilters = cleanFilters({
            ...commonFilters,
            mob_num:
              searchType === "mob_num" && searchText ? searchText : undefined,
            user_id:
              searchType === "user_id" && searchText ? searchText : undefined,
            loan_id:
              searchType === "loan_id" && searchText ? searchText : undefined,
          });
          const entities = await fetchAllForExport<AgentEntity>(
            async (page, pageSize) => {
              const res = await entityRoleMappingApi.getAgentEntities({
                ...baseEntityRequest,
                page,
                page_size: pageSize,
                filters: entityFilters,
                text:
                  (!searchType || searchType === "name") && searchText
                    ? searchText
                    : undefined,
              });
              if (res.status !== "Success" || !res.response) return { rows: [] };
              return {
                rows: res.response.response,
                hasMore: res.response.pagination?.has_more,
              };
            },
            exportLimit,
            EXPORT_PAGE_SIZE,
          );
          dataToExport = entities.map((e) => convertEntityToLoan(e));
        }
      } else {
        const sortByValue: { [key: string]: 1 | -1 } = sortBy
          ? { [sortBy]: sortOrder === "asc" ? 1 : -1 }
          : { created_at: -1 };
        const effectiveAgentId = (filters.filter_agent_id as string) || agentId;

        const baseRequest = {
          query_type: "new_get_agent_loans" as const,
          agent_id: effectiveAgentId,
          ranges: { created_at: createdAtRange, updated_at: updatedAtRange, disbursed_date: disbursedDateRange },
          sort_by: sortByValue,
        };

        if (isAllSearch) {
          const searchTargets = ["loan_id", "mob_num", "user_id", "name"];
          const seenLoanIds = new Set<string>();
          for (const type of searchTargets) {
            const specificFilters = { ...commonFilters };
            let specificText: string | undefined = undefined;
            if (type === "name") specificText = searchText;
            else if (type === "user_id")
              (specificFilters as any)["l.user_id"] = searchText;
            else (specificFilters as any)[type] = searchText;

            const loans = await fetchAllForExport<Loan>(
              async (page, pageSize) => {
                const res = await loansApi.getLoanDetails({
                  ...baseRequest,
                  page,
                  page_size: pageSize,
                  filters: specificFilters,
                  text: specificText,
                });
                return {
                  rows: res.response || [],
                  hasMore: res.pagination_metadata?.has_more,
                };
              },
              exportLimit,
              EXPORT_PAGE_SIZE,
            );

            loans.forEach((loan) => {
              if (!seenLoanIds.has(loan.loan_id)) {
                seenLoanIds.add(loan.loan_id);
                dataToExport.push(loan);
              }
            });
          }
        } else {
          const requestFilters = cleanFilters({
            ...commonFilters,
            loan_id:
              searchType === "loan_id" && searchText ? searchText : undefined,
            mob_num:
              searchType === "mob_num" && searchText ? searchText : undefined,
            "l.user_id":
              searchType === "user_id" && searchText ? searchText : undefined,
          });
          dataToExport = await fetchAllForExport<Loan>(
            async (page, pageSize) => {
              const res = await loansApi.getLoanDetails({
                ...baseRequest,
                page,
                page_size: pageSize,
                filters: requestFilters,
                text:
                  (!searchType || searchType === "name") && searchText
                    ? searchText
                    : undefined,
              });
              return {
                rows: res.response || [],
                hasMore: res.pagination_metadata?.has_more,
              };
            },
            exportLimit,
            EXPORT_PAGE_SIZE,
          );
        }
      }

      if (dataToExport.length === 0) {
        alert("No data found to export.");
        return;
      }

      const exportColumns: ExportColumn<Loan>[] = targetColumns.map((col) => {
        if (!col.render) return { key: col.key, label: col.label };
        return {
          key: col.key,
          label: col.label,
          render: (value: unknown, row: Loan) => {
            const rawValue = (row as unknown as Record<string, unknown>)[
              col.key
            ];
            if (col.key === "name") {
              const name = [row.fname, row.mname, row.lname]
                .filter(Boolean)
                .join(" ");
              return name || "-";
            }
            if (col.key === "sub_status" || col.key === "loan_status")
              return String(rawValue || "-");
            if (
              col.key === "requested_amt" ||
              col.key === "lender_approved_amt" ||
              col.key === "disbursed_amt"
            )
              return rawValue ? Number(rawValue) : 0;
            if (col.key === "loan_interest_rate")
              return rawValue ? `${Number(rawValue)}%` : "-";
            if (
              col.key.includes("_date") ||
              col.key.includes("created_at") ||
              col.key.includes("updated_at")
            ) {
              if (!rawValue) return "-";
              try {
                return new Date(rawValue as string).toLocaleString();
              } catch {
                return String(rawValue);
              }
            }
            if (rawValue === null || rawValue === undefined) return "-";
            return String(rawValue);
          },
        };
      });

      exportToExcel(
        dataToExport as unknown as Record<string, unknown>[],
        exportColumns as unknown as ExportColumn<Record<string, unknown>>[],
        "loans",
      );
    } catch (err) {
      console.error(err);
      const errorMsg =
        err instanceof Error ? err.message : "Failed to export data";
      setError(errorMsg);
      alert(`Export Failed: ${errorMsg}`);
    } finally {
      setExporting(false);
    }
  };

  const filterConfigs: FilterConfig[] = [
    {
      key: "search_type",
      label: "Search Type",
      type: "select",
      options: SEARCH_TYPE_OPTIONS,
    },
    {
      key: "search_text",
      label: "Search",
      type: "text",
      placeholder: "Enter search text...",
    },
    {
      key: "sub_status",
      label: "Sub Status",
      type: "multiSelect",
      options: SUB_STATUS_OPTIONS,
    },
    {
      key: "loan_status",
      label: "Loan Status",
      type: "multiSelect",
      options: LOAN_STATUS_OPTIONS,
    },
    {
      key: "lender_id",
      label: "Lender",
      type: "select",
      options: LENDER_OPTIONS,
    },
    {
      key: "filter_agent_id",
      label: "Agent",
      type: "searchableSelect",
      options: subAgentOptions,
      placeholder: "Select agent...",
    },
    ...(selectedRole?.role_type === "DOCUMENT_VERIFIER"
      ? [
          {
            key: "assigned_to_agent",
            label: "Assigned To",
            type: "select",
            options: [
              { label: "All Agents", value: "" },
              ...availableAgents.map((agent) => ({
                label: agent.agent_name,
                value: agent.role_id,
              })),
            ],
          } as FilterConfig,
        ]
      : []),
    {
      key: "date_range",
      label: "Date Range",
      type: "dateRangeField",
      dateRangeFields: [
        { label: "Created At", value: "created_at" },
        { label: "Updated At", value: "updated_at" },
        { label: "Disbursed Date", value: "disbursed_date" },
      ],
    },
  ];

  const columns: Column<Loan>[] = [
    { key: "loan_id", label: "Loan ID", sortable: true, defaultVisible: true },
    {
      key: "name",
      label: "Name",
      sortable: true,
      defaultVisible: true,
      render: (_, row) =>
        [row.fname, row.mname, row.lname].filter(Boolean).join(" ") || "-",
    },
    { key: "mob_num", label: "Mobile", sortable: true, defaultVisible: true },
    {
      key: "sub_status",
      label: "Status",
      sortable: true,
      defaultVisible: true,
      render: (value) => (
        <span
          style={{
            color:
              {
                DISBURSED: "#10b981",
                APPROVED: "#10b981",
                PENDING: "#f59e0b",
                REJECTED: "#ef4444",
                UNDER_REVIEW: "#3b82f6",
                KYC_SUCCESS: "#10b981",
                KYC_REJECTED: "#ef4444",
                FRESH_LOAN: "#6b7280",
                LOAN_DETAILS_SUBMITTED: "#3b82f6",
                BANK_ADDED: "#3b82f6",
                SIGN_AGREEMENT: "#f59e0b",
                CANCELLED: "#ef4444",
                SKIP: "#9ca3af",
              }[String(value || "")] || "#6b7280",
            fontWeight: 500,
          }}
        >
          {String(value || "")}
        </span>
      ),
    },
    {
      key: "requested_amt",
      label: "Requested Amount",
      sortable: true,
      defaultVisible: true,
      render: (value) => (value ? `₹${Number(value).toLocaleString()}` : "-"),
    },
    {
      key: "lender_approved_amt",
      label: "Approved Amount",
      sortable: true,
      defaultVisible: true,
      render: (value) => (value ? `₹${Number(value).toLocaleString()}` : "-"),
    },
    {
      key: "disbursed_amt",
      label: "Disbursed Amount",
      sortable: true,
      defaultVisible: false,
      render: (value) => (value ? `₹${Number(value).toLocaleString()}` : "-"),
    },
    {
      key: "loan_tenure",
      label: "Tenure (Months)",
      sortable: true,
      defaultVisible: false,
    },
    {
      key: "loan_interest_rate",
      label: "Interest Rate",
      sortable: true,
      defaultVisible: false,
      render: (value) => (value ? `${Number(value)}%` : "-"),
    },
    {
      key: "disbursed_date",
      label: "Disbursed Date",
      sortable: true,
      defaultVisible: false,
      render: (value) => {
        try {
          return value ? new Date(value as string).toLocaleDateString() : "-";
        } catch {
          return String(value);
        }
      },
    },
    {
      key: "loan_created_at",
      label: "Created At",
      sortable: true,
      defaultVisible: true,
      render: (value) => {
        try {
          return value ? new Date(value as string).toLocaleDateString() : "-";
        } catch {
          return String(value);
        }
      },
    },
    {
      key: "loan_updated_at",
      label: "Updated At",
      sortable: true,
      defaultVisible: false,
      render: (value) => {
        try {
          return value ? new Date(value as string).toLocaleDateString() : "-";
        } catch {
          return String(value);
        }
      },
    },
    {
      key: "agent_name",
      label: "Sub Agent Name",
      sortable: true,
      defaultVisible: true,
      render: (value, row) => (
        <CallAgentButton
          agentName={
            value
              ? String(value)
              : subAgentOptions.find((a) => a.value === row.agent_id)?.label ||
                undefined
          }
          agentNumber={row.agent_mob_num ?? row.agent_mobile}
        />
      ),
    },
    {
      key: "parent_agent_name",
      label: "Parent Agent Name",
      sortable: true,
      defaultVisible: true,
      render: (value, row) => (
        <CallAgentButton
          agentName={value ? String(value) : undefined}
          agentNumber={row.parent_agent_mob_num}
        />
      ),
    },
    {
      key: "loan_type",
      label: "Loan Type",
      sortable: true,
      defaultVisible: false,
    },
    {
      key: "payment_frequency",
      label: "Payment Frequency",
      sortable: true,
      defaultVisible: false,
    },
    ...(selectedRole?.role_type === "DOCUMENT_VERIFIER"
      ? [
          {
            key: "assigned_to_agent",
            label: "Assigned To",
            sortable: true,
            defaultVisible: true,
            render: (value, row): ReactNode => {
              const currentAssignedRoleId = (
                row as unknown as Record<string, unknown>
              ).assigned_agent_id as string | undefined;
              const isChanging = changingAssignment === row.loan_id;
              if (isChanging)
                return (
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <select
                      value={newAssignedAgent}
                      onChange={(e) => setNewAssignedAgent(e.target.value)}
                      style={{
                        padding: "4px 8px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="">Select Agent</option>
                      {availableAgents.map((agent) => (
                        <option key={agent.role_id} value={agent.role_id}>
                          {agent.agent_name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (newAssignedAgent)
                          handleChangeAssignment(
                            row.loan_id,
                            currentAssignedRoleId,
                          );
                      }}
                      disabled={!newAssignedAgent}
                      style={{
                        padding: "4px 12px",
                        background: "#1565d8",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: newAssignedAgent ? "pointer" : "not-allowed",
                        opacity: newAssignedAgent ? 1 : 0.5,
                      }}
                    >
                      Save
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setNewAssignedAgent("");
                        setChangingAssignment(null);
                      }}
                      style={{
                        padding: "4px 12px",
                        background: "#6b7280",
                        color: "#ffffff",
                        border: "none",
                        borderRadius: "4px",
                        fontSize: "12px",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                );
              const assignedAgent = availableAgents.find(
                (a) => a.role_id === currentAssignedRoleId,
              );
              return (
                <div
                  style={{ display: "flex", gap: "8px", alignItems: "center" }}
                >
                  <span>
                    {assignedAgent?.agent_name ||
                      (value ? String(value) : "Unassigned")}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setChangingAssignment(row.loan_id);
                      setNewAssignedAgent(currentAssignedRoleId || "");
                    }}
                    style={{
                      padding: "4px 8px",
                      background: "#f3f4f6",
                      color: "#374151",
                      border: "1px solid #d1d5db",
                      borderRadius: "4px",
                      fontSize: "12px",
                      cursor: "pointer",
                    }}
                    title="Change assignment"
                  >
                    Change
                  </button>
                </div>
              );
            },
          } as Column<Loan>,
        ]
      : []),
  ];

  const dynamicColumns = useMemo(() => {
    if (loans.length === 0) return [];
    const allKeys = Object.keys(loans[0] || {}) as (keyof Loan)[];
    const definedKeys = new Set(columns.map((col) => col.key));
    const remainingKeys = allKeys.filter(
      (key) => !definedKeys.has(String(key)),
    );
    return remainingKeys.map((key) => {
      const label = String(key)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
      let render: ((value: unknown, row: Loan) => React.ReactNode) | undefined;
      if (
        key.toString().includes("_amt") ||
        key.toString().includes("amount") ||
        key === "income" ||
        key === "total_pre_disbursal_charges"
      )
        render = (value) =>
          value ? `₹${Number(value).toLocaleString()}` : "-";
      else if (
        key.toString().includes("percentage") ||
        key.toString().includes("rate") ||
        key.toString().includes("apr")
      )
        render = (value) => (value ? `${String(value)}%` : "-");
      else if (
        key.toString().includes("_date") ||
        key.toString().includes("created_at") ||
        key.toString().includes("updated_at") ||
        key === "dob"
      )
        render = (value) => {
          try {
            return value ? new Date(value as string).toLocaleDateString() : "-";
          } catch {
            return String(value);
          }
        };
      else if (
        key === "is_active" ||
        key === "is_politically_exposed" ||
        key === "salaried"
      )
        render = (value) =>
          value === null || value === undefined ? "-" : value ? "Yes" : "No";
      else
        render = (value) =>
          value === null || value === undefined ? "-" : String(value);
      return {
        key: String(key),
        label,
        sortable: true,
        defaultVisible: false,
        render,
      } as Column<Loan>;
    });
  }, [loans]);

  const allColumns = useMemo(
    () => [...columns, ...dynamicColumns],
    [columns, dynamicColumns],
  );

  const statusColors: Record<string, string> = {
    DISBURSED: "#10b981",
    APPROVED: "#10b981",
    PENDING: "#f59e0b",
    REJECTED: "#ef4444",
    UNDER_REVIEW: "#3b82f6",
    KYC_SUCCESS: "#10b981",
    KYC_REJECTED: "#ef4444",
    FRESH_LOAN: "#6b7280",
    LOAN_DETAILS_SUBMITTED: "#3b82f6",
    BANK_ADDED: "#3b82f6",
    SIGN_AGREEMENT: "#f59e0b",
    CANCELLED: "#ef4444",
    SKIP: "#9ca3af",
    Active: "#10b981",
    Closed: "#6b7280",
    Pending: "#f59e0b",
    Rejected: "#ef4444",
    "Esign Completed": "#10b981",
  };

  const mockLoanCounts = useMemo(
    () => [
      {
        title: "Loan Status",
        items: LOAN_STATUS_OPTIONS.map((opt) => ({
          label: opt.label,
          count: Math.floor(Math.random() * 100) + 10,
          color: statusColors[opt.value] || "#6b7280",
        })),
      },
      {
        title: "Sub Status",
        items: SUB_STATUS_OPTIONS.map((opt) => ({
          label: opt.label.replace(/_/g, " "),
          count: Math.floor(Math.random() * 50) + 1,
          color: statusColors[opt.value] || "#6b7280",
        })),
      },
    ],
    [],
  );

  if (!isAuthenticated) return null;

  return (
    <MainLayout>
      <div className={styles.pageContainer}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>
              Loans
              {agentName && (
                <span className={styles.agentContext}>({agentName})</span>
              )}
            </h1>
            {agentName && (
              <p className={styles.agentContextInfo}>
                Viewing loans for: {agentName} (ID: {agentIdOverride})
              </p>
            )}
          </div>
          <div className={styles.headerActions}>
            <select
              value={exportLimit}
              onChange={(e) => setExportLimit(Number(e.target.value))}
              className={styles.limitSelect}
              title="Select number of records to export"
            >
              <option value={10}>Export 10</option>
              <option value={50}>Export 50</option>
              <option value={100}>Export 100</option>
              <option value={500}>Export 500</option>
              <option value={1000}>Export 1000</option>
              <option value={5000}>Export 5000</option>
            </select>
            <button
              className={styles.exportButton}
              onClick={() => handleExportToExcel()}
              disabled={loading || exporting || loans.length === 0}
              title="Export to Excel"
            >
              {exporting ? (
                "Exporting..."
              ) : (
                <>
                  <ExcelIcon size={18} />
                  Export to Excel
                </>
              )}
            </button>
          </div>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
        <FilterPanel
          filters={filterConfigs}
          values={{
            ...filters,
            search_text: searchTextInput,
          }}
          onChange={handleFilterChange}
          onSearchSubmit={handleSearchSubmit}
          onReset={handleResetFilters}
          statusCounts={mockLoanCounts}
        />
        <DataTable
          data={loans}
          columns={allColumns}
          loading={loading}
          tableId={selectedRole ? `loans_${selectedRole.role_type}` : "loans"}
          pagination={{
            page,
            pageSize,
            hasMore,
            onPageChange: setPage,
            onPageSizeChange: (newSize) => {
              setPageSize(newSize);
              setPage(1);
            },
          }}
          sorting={{ sortBy, sortOrder, onSort: handleSort }}
          onRowActions={[
            {
              label: "View",
              variant: "primary",
              onClick: (row) => {
                const params = new URLSearchParams();
                if (agentIdOverride) params.set("agentId", agentIdOverride);
                if (agentName) params.set("agentName", agentName);
                const queryString = params.toString();
                navigate(
                  `/loans/${row.loan_id}${queryString ? `?${queryString}` : ""}`,
                  { state: { loan: row } },
                );
              },
            },
            {
              label: callingRowId ? "Calling..." : "Phone",
              icon: "\u{1F4F1}",
              variant: "tertiary",
              onClick: (row) => handleCallCustomer(row, "phone"),
              disabled: (row) => !row.mob_num || callingRowId === row.loan_id,
            },
          ]}
          emptyMessage="No loans found"
        />
        <Toast
          message={callToast.message}
          show={callToast.show}
          onClose={() => setCallToast({ show: false, message: "" })}
        />
      </div>
    </MainLayout>
  );
};
